import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import BottleCapacityForm from './components/BottleCapacityForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import {
  BottleCapacityPayload,
  useGetBottleCapacityLazyQuery,
  useUpdateBottleCapacityMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  EDIT_BOTTLE_CAPACITY_TEXT,
  bottleCapacityInitialValue,
  BOTTLE_CAPACITY_IS_UPDATED_TEXT,
} from 'constants/index';
import { createBottleCapacityInput } from 'lib/helper';
import { bottleCapacityTypeSchema } from 'validationSchema';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import { AddBottleCapacityFormType, ParamType } from 'interfaces';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const bottleCapacityMethods = useForm<AddBottleCapacityFormType>({
    defaultValues: bottleCapacityInitialValue,
    resolver: yupResolver(bottleCapacityTypeSchema),
  });

  const { handleSubmit: handleBottleCapacitySubmit, setValue: setBottleCapacityValue } =
    bottleCapacityMethods;

  const [updateBottleCapacity, { loading: productTypeLoading }] = useUpdateBottleCapacityMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateBottleCapacity } = data;
      const { response } = updateBottleCapacity || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getBottleCapacity, { loading: getLoading }] = useGetBottleCapacityLazyQuery({
    onCompleted: (data) => {
      const { getBottleCapacity } = data || {};
      const { response, bottleCapacity } = getBottleCapacity || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(bottleCapacity as BottleCapacityPayload['bottleCapacity']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onBottlePriceSubmit: SubmitHandler<AddBottleCapacityFormType> = async (data) => {
    const input = createBottleCapacityInput(data);

    await updateBottleCapacity({
      variables: {
        updateBottleCapacityInput: {
          id: id || '',
          ...input,
        },
      },
    });
  };

  const fetchBottlePrice = useCallback(async () => {
    id &&
      (await getBottleCapacity({
        variables: {
          findBottleCapacityInput: {
            id,
          },
        },
      }));
  }, [id, getBottleCapacity]);

  const setValues = (params: BottleCapacityPayload['bottleCapacity']) => {
    const { lohCost, fillPercentage, bottleSize, productType, productSize, servingSize } = params || {};
    const { name: bottleSizeName, id } = bottleSize || {};
    const { name: productTypeName, id: productTypeId, type } = productType || {};
    const { name: productSizeName, id: productSizeId } = productSize || {};
    const { size, id: servingSizeId } = servingSize || {};

    setBottleCapacityValue('lohCost', `${lohCost}` || '0');
    setBottleCapacityValue('fillingPercentage', `${fillPercentage}` || '0');
    setBottleCapacityValue('bottleSize', {
      value: id ?? '',
      name: bottleSizeName ?? '',
    });
    setBottleCapacityValue('productSize', {
      value: productSizeId ?? '',
      name: productSizeName ?? '',
    });
    setBottleCapacityValue('productType', {
      value: productTypeId ?? '',
      name: productTypeName ?? '',
      type: type ?? '',
    });
    setBottleCapacityValue('servingSize', {
      value: servingSizeId ?? '',
      name: `${size}` ?? '',
    });
  };

  useEffect(() => {
    id && fetchBottlePrice();
  }, [id, fetchBottlePrice]);

  const loading = productTypeLoading || getLoading;

  return (
    <Fragment>
      <Box sx={flexCenterBetween}>
        <Typography variant="h5">{EDIT_BOTTLE_CAPACITY_TEXT}</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={6} md={12}>
          <FormProvider {...bottleCapacityMethods}>
            <form onSubmit={handleBottleCapacitySubmit(onBottlePriceSubmit)}>
              <BottleCapacityForm loading={loading} isEdit />
            </form>
          </FormProvider>
        </Grid>
      </Grid>

      <BackdropLoader open={loading} text={BOTTLE_CAPACITY_IS_UPDATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
